import { Switch } from '@syncfusion/ej2-buttons';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { InPlaceEditor } from '@syncfusion/ej2-inplace-editor';

import { routes } from '@/shared/routes';
import { OrganisationType } from '@/shared/types/enum';
import {
  COMPANY_REGISTRATION_ORGANISATION_TYPES,
  hidesAccountsAndConfirmationStatement,
  isCharityOrganisationType,
  isCharityToggleEnabledOrganisationType,
} from '@/shared/utils/organisationType';

import { refreshCharityNumberLink } from '@/front/charityRegisterLink';
import { getControl, getEditor, getProps } from '@/front/editable/state';
import { listen } from '@/front/event';
import { POST } from '@/front/fetch';

const getEditableValue = (id: string) => getControl(id)?.value ?? getEditor(id)?.value;

const setEditableValue = (id: string, value: unknown) => {
  const control = getControl(id);
  const editor = getEditor(id);

  if (control) {
    control.value = value as never;
  } else if (editor) {
    editor.value = value as never;
  }
};

const setEditableEnabled = (id: string, enabled: boolean) => {
  const control = getControl(id) as { enabled: boolean } | undefined;
  const editor = getEditor(id);

  if (control) {
    control.enabled = enabled;
  } else if (editor) {
    editor.disabled = !enabled;
  }
};

listen('editablesReady', () => {
  [...document.querySelectorAll('.editable[data-field="organisationType"]')]
    .map((editable) => editable as HTMLDivElement)
    .forEach((editable) => {
      const form = editable.closest('form') as HTMLFormElement;
      if (!form) return;

      const organisationTypeDropDownList = getControl(editable.id) as DropDownList | undefined;
      const organisationTypeEditor = getEditor(editable.id) as InPlaceEditor | undefined;
      const organisationTypeControl = organisationTypeDropDownList ?? organisationTypeEditor;

      if (!organisationTypeControl) {
        return;
      }

      const manualChWarning = form.querySelector('[data-manual-ch-warning="true"]') as HTMLElement | null;

      const updateManualChWarning = () => {
        const selected = getEditableValue(editable.id) as OrganisationType;

        const shouldShow =
          selected === OrganisationType.LimitedCompany || selected === OrganisationType.LimitedLiabilityPartnership;

        manualChWarning?.classList.toggle('d-none', !shouldShow);
      };

      const getCharitySwitchEditable = () =>
        form.querySelector('[data-charity-switch-row] .editable') as HTMLDivElement | null;

      const getCharitySwitch = () => {
        const charitySwitchEditable = getCharitySwitchEditable();
        return charitySwitchEditable ? (getControl(charitySwitchEditable.id) as Switch | undefined) : undefined;
      };

      const refresh = (organisationTypes: readonly OrganisationType[]) => {
        const selected = organisationTypes.includes(getEditableValue(editable.id) as OrganisationType);

        [...form.querySelectorAll(`[data-organisation-types="${organisationTypes.join(',')}"]`)]
          .map((childContainer) => childContainer as HTMLElement)
          .forEach((childContainer) => {
            childContainer.classList.toggle('d-none', !selected);

            const childEditable = childContainer.querySelector('.editable') as HTMLDivElement;

            if (childEditable) {
              if (childContainer.hasAttribute('data-charity-switch-row')) {
                return;
              }
              setEditableEnabled(childEditable.id, selected);
            }
          });
      };

      let suppressingCharitySwitchChange = false;

      const refreshCharityFields = () => {
        const organisationType = getEditableValue(editable.id) as OrganisationType | null | undefined;
        const hasOrganisationType = !!organisationType;
        const isCharityType = isCharityOrganisationType(organisationType);
        const charityToggleEnabled = isCharityToggleEnabledOrganisationType(organisationType);
        const charitySwitch = getCharitySwitch();

        const charitySwitchRow = form.querySelector('[data-charity-switch-row]') as HTMLElement | null;
        charitySwitchRow?.classList.toggle('d-none', !hasOrganisationType);

        if (charitySwitch) {
          suppressingCharitySwitchChange = true;
          try {
            charitySwitch.checked = isCharityType;
            charitySwitch.disabled = !charityToggleEnabled;
          } finally {
            suppressingCharitySwitchChange = false;
          }
        }

        const showCharityNumber = isCharityType;

        const charityNumberRow = form.querySelector('[data-charity-number-row]') as HTMLElement | null;
        if (charityNumberRow) {
          charityNumberRow.classList.toggle('d-none', !showCharityNumber);

          const charityNumberEditable = charityNumberRow.querySelector('.editable') as HTMLDivElement | null;
          if (charityNumberEditable) {
            const props = getProps(charityNumberEditable.id);
            setEditableEnabled(charityNumberEditable.id, showCharityNumber && !props?.readOnly);
            refreshCharityNumberLink(charityNumberEditable);
          }
        }

        const charityAnnualFilingSection = form.querySelector(
          '[data-charity-annual-filing-section]',
        ) as HTMLElement | null;
        if (charityAnnualFilingSection) {
          charityAnnualFilingSection.classList.toggle('d-none', !showCharityNumber);
        }

        const hideAccountsAndConfirmationStatement = hidesAccountsAndConfirmationStatement(organisationType);
        for (const selector of ['[data-accounts-section]', '[data-confirmation-statement-section]'] as const) {
          const section = form.querySelector(selector) as HTMLElement | null;
          section?.classList.toggle('d-none', hideAccountsAndConfirmationStatement);
        }
      };

      const applyOrganisationType = async (organisationType: OrganisationType) => {
        setEditableValue(editable.id, organisationType);

        const modelId = getProps(editable.id)?.modelId;
        if (modelId) {
          await POST(routes.data.editable({ model: 'Organisation' }), {
            params: {
              name: 'organisationType',
              primaryKey: modelId,
              value: organisationType,
            },
          });
        }

        if (organisationTypeDropDownList) {
          organisationTypeDropDownList.dataBind?.();
        }

        refreshAll();
      };

      const refreshAll = () => {
        refresh(COMPANY_REGISTRATION_ORGANISATION_TYPES);
        refresh([OrganisationType.SoleTrader, OrganisationType.Partnership]);
        refresh([OrganisationType.SoleTrader]);
        refreshCharityFields();
        updateManualChWarning();
      };

      refreshAll();

      if (organisationTypeDropDownList) {
        organisationTypeDropDownList.addEventListener('change', refreshAll);
      } else if (organisationTypeEditor) {
        organisationTypeEditor.addEventListener('actionSuccess', refreshAll);
      }

      const charitySwitch = getCharitySwitch();
      if (charitySwitch) {
        charitySwitch.change = () => {
          if (suppressingCharitySwitchChange) {
            return;
          }

          const organisationType = getEditableValue(editable.id) as OrganisationType;
          if (!isCharityToggleEnabledOrganisationType(organisationType)) {
            refreshCharityFields();
            return;
          }

          const nextOrganisationType = charitySwitch.checked
            ? OrganisationType.CharitableCompany
            : OrganisationType.LimitedCompany;

          if (organisationType === nextOrganisationType) {
            return;
          }

          applyOrganisationType(nextOrganisationType);
        };
      }
    });
});
